import type { CSSProperties, ReactNode } from 'react';
import { Fragment } from 'react';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import type { IconDefinition } from '@sorare/core/src/atoms/icons';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconHourglass } from '@sorare/core/src/atoms/icons/system/iconHourglass.ts';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, LabelS } from '@sorare/core/src/atoms/typography';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import { css } from '@sorare/css';

type MarketHeroCta = {
  label: ReactNode;
  to: string;
  icon?: IconDefinition;
};

export type MarketHeroTag = {
  key: string;
  icon?: IconDefinition | { pictureUrl: string };
  label: ReactNode;
};

type Props = {
  backgroundUrl: string;
  mobileBackgroundUrl?: string;
  subtitle: ReactNode;
  primaryCta: MarketHeroCta;
  secondaryCta?: MarketHeroCta;
  icon?: ReactNode;
  countdown?: Date;
  tags?: MarketHeroTag[];
};

const MAX_TAGS = 3;

const root = css`
  --hero-inline-padding: var(--container-padding, var(--double-unit));
  --hero-background-url: var(
    --hero-mobile-background-url,
    var(--hero-desktop-background-url)
  );
  --hero-fade: linear-gradient(
    180deg,
    rgba(var(--c-rgb-black), 0) 0%,
    var(--c-black) 78.85%
  );

  position: relative;
  isolation: isolate;
  padding: 0 var(--hero-inline-padding) calc(7 * var(--unit));
  background:
    /* Overshoot by a pixel: a fractional element height snaps the fade's
       raster short of the box, revealing a hairline of the image. */
    var(--hero-fade) top center / 100% calc(100% + 1px) no-repeat,
    var(--hero-background-url) center / cover no-repeat,
    var(--c-black);

  @media ${tabletAndAbove} {
    --hero-background-url: var(--hero-desktop-background-url);
    --hero-fade: none;

    justify-content: center;
    min-height: calc(58 * var(--unit));
    padding: calc(11 * var(--unit)) var(--hero-inline-padding)
      calc(6 * var(--unit));
  }
`;

const content = css`
  align-items: flex-start;
  padding-top: calc(12.5 * var(--unit));

  @media ${tabletAndAbove} {
    max-width: calc(56 * var(--unit));
    padding-top: 0;
  }
`;

const countdownBadge = css`
  align-self: flex-start;
`;

const iconSlot = css`
  height: calc(18 * var(--unit));
  width: calc(18 * var(--unit));

  & > * {
    height: 100%;
    width: 100%;
    object-fit: contain;
    object-position: left center;
  }

  @media ${tabletAndAbove} {
    height: calc(20 * var(--unit));
    width: calc(20 * var(--unit));
  }
`;

const ctaRow = css`
  align-self: stretch;

  & > * {
    flex: 1;
  }

  @media ${tabletAndAbove} {
    align-self: auto;

    & > * {
      flex: initial;
    }
  }
`;

const tagsRow = css`
  flex-wrap: wrap;
  row-gap: var(--unit);
`;

const tagPicture = css`
  height: var(--double-unit);
  width: var(--double-unit);
  object-fit: contain;
`;

const separator = css`
  display: none;
  color: rgba(var(--c-rgb-white), 0.2);

  @media ${tabletAndAbove} {
    display: inline;
  }
`;

const TagIcon = ({ icon }: { icon: NonNullable<MarketHeroTag['icon']> }) =>
  'pictureUrl' in icon ? (
    <img className={tagPicture} src={icon.pictureUrl} alt="" />
  ) : (
    <FontAwesomeIcon icon={icon} fontSize={16} />
  );

const CtaButton = ({
  cta,
  primary,
}: {
  cta: MarketHeroCta;
  primary: boolean;
}) => (
  <Button
    size="large"
    color={primary ? 'primary' : 'secondary'}
    to={cta.to}
    startIcon={cta.icon ? <FontAwesomeIcon icon={cta.icon} /> : undefined}
  >
    {cta.label}
  </Button>
);

export const MarketHero = ({
  backgroundUrl,
  mobileBackgroundUrl,
  subtitle,
  primaryCta,
  secondaryCta,
  icon,
  countdown,
  tags = [],
}: Props) => (
  <Vertical
    gap={3}
    className={root}
    style={
      {
        '--hero-desktop-background-url': `url(${backgroundUrl})`,
        ...(mobileBackgroundUrl && {
          '--hero-mobile-background-url': `url(${mobileBackgroundUrl})`,
        }),
      } as CSSProperties
    }
  >
    <Vertical gap={3} className={content}>
      {!!countdown && (
        <Button
          asSpan
          brand
          size="small"
          color="tertiary"
          className={countdownBadge}
          startIcon={<FontAwesomeIcon icon={iconHourglass} />}
        >
          <TimeLeft time={countdown} hideTooltip />
        </Button>
      )}
      {!!icon && <div className={iconSlot}>{icon}</div>}
      <BodyM>{subtitle}</BodyM>
      <Horizontal gap={1} className={ctaRow}>
        <CtaButton cta={primaryCta} primary />
        {!!secondaryCta && <CtaButton cta={secondaryCta} primary={false} />}
      </Horizontal>
    </Vertical>
    {tags.length > 0 && (
      <Horizontal gap={2} className={tagsRow}>
        {tags.slice(0, MAX_TAGS).map(({ key, icon: tagIcon, label }, index) => (
          <Fragment key={key}>
            {index > 0 && <span className={separator}>|</span>}
            <Horizontal gap={1} center>
              {!!tagIcon && <TagIcon icon={tagIcon} />}
              <LabelS bold>{label}</LabelS>
            </Horizontal>
          </Fragment>
        ))}
      </Horizontal>
    )}
  </Vertical>
);
